<template>
  <div class="assortment-groups-tab pa-2">
    <v-row no-gutters class="mb-3">
      <v-col :cols="8" class="available-ags pr-3 pb-2">
        <h3 class="pt-1 pb-3">
          {{ tkey('availableAGs') }}
        </h3>
        <div class="available-ags__wrapper">
          <v-row no-gutters>
            <v-col v-if="hasTemplateAvailable" class="px-2 py-1">
              <v-btn-toggle
                v-model="isTemplateOnly"
                mandatory
                @update:model-value="gridApi.onFilterChanged()"
              >
                <v-btn :value="false">
                  {{ tkey('toggle.all') }}
                </v-btn>
                <v-btn :value="true">
                  {{ tkey('toggle.templated') }}
                </v-btn>
              </v-btn-toggle>
            </v-col>

            <v-col class="px-2 py-1 d-flex justify-end">
              <RtlsSearch
                v-model="searchString"
                :placeholder="$t('workpackagePage.scope.searchLabel')"
                @update:model-value="gridApi.onFilterChanged()"
              />
            </v-col>

            <v-col v-if="syntheticFurnitureEnabled" class="px-2 py-1">
              <v-btn-toggle
                v-model="syntheticFurnitureFilter"
                mandatory
                @update:model-value="gridApi.onFilterChanged()"
              >
                <v-btn value="standard_only">
                  {{ tkey('toggleSynthetic.standard') }}
                </v-btn>
                <v-btn value="synthetic_only">
                  {{ tkey('toggleSynthetic.synthetic') }}
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>

          <div class="h-100 flex-grow-1">
            <AgGridVue
              style="height: 360px"
              class="w-100"
              auto-params-refresh
              data-id-e2e="listAvailableAssortmentGroups"
              :column-defs="columnDefs"
              :row-data="assortmentGroups"
              :does-external-filter-pass="doesExternalFilterPass"
              :grid-options="gridOptions"
              :stop-editing-when-cells-loses-focus="true"
              :loading="isLoadingAssortmentGroups"
              @grid-ready="onGridReady"
            />
          </div>
        </div>
      </v-col>

      <v-col class="pl-3 pb-2">
        <v-row v-if="!hideAnalysisOnlySelection" no-gutters>
          <v-col>
            <h3>{{ tkey('analysisOnly') }}</h3>
          </v-col>
          <v-col class="text-right">
            <v-btn
              class="assortment-groups-tab__link"
              variant="text"
              size="small"
              :disabled="selectedAssortmentGroupsForAnalysisOnly.length === 0"
              @click="unselectAllAnalysis"
            >
              <v-icon size="16"> mdi-close </v-icon>
              <span>{{ $t('actions.clearAll') }}</span>
            </v-btn>
          </v-col>
        </v-row>
        <div v-if="!hideAnalysisOnlySelection" class="grid-wrapper">
          <div class="grid" data-id-e2e="listSelectedAssortmentGroupsForAnalysisOnly">
            <div
              v-for="ag in selectedAssortmentGroupsForAnalysisOnly"
              :key="ag.key"
              class="display-contents"
            >
              <div class="item trimmed-text">
                {{ ag.text }}
              </div>
              <div class="item item__icon">
                <v-btn size="x-small" icon @click="unselect('analysis', [ag.key])">
                  <v-icon size="20"> $trash </v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <v-row no-gutters>
          <v-col>
            <h3>{{ tkey('includeForAssortment') }}</h3>
          </v-col>
          <v-col class="text-right">
            <v-btn
              class="assortment-groups-tab__link"
              variant="text"
              size="small"
              :disabled="selectedAssortmentGroupsForAssortment.length === 0"
              @click="unselectAllAssortment"
            >
              <v-icon size="16"> mdi-close </v-icon>
              <span>{{ $t('actions.clearAll') }}</span>
            </v-btn>
          </v-col>
        </v-row>

        <div :class="['grid-wrapper', { 'grid-wrapper-extended': hideAnalysisOnlySelection }]">
          <div class="grid" data-id-e2e="listSelectedAssortmentGroupsForAssortment">
            <div
              v-for="ag in selectedAssortmentGroupsForAssortment"
              :key="ag.key"
              class="display-contents"
            >
              <div class="item trimmed-text">
                {{ ag.text }}
              </div>
              <div class="item item__icon">
                <v-btn size="x-small" icon @click="unselect('assortment', [ag.key])">
                  <v-icon size="20"> $trash </v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
        <v-row no-gutters>
          <v-col>
            <h3 class="mb-2">
              {{ $t('workpackagePage.scope.productsAndStoresLabel') }}
            </h3>
            <p class="mb-2">{{ $t('workpackagePage.scope.importProductDataLabel') }}:</p>
            <ImportFromPlanogram v-if="datesLoaded" :custom-workpackage="customWorkpackage" />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-divider />

    <v-row class="mt-3" no-gutters>
      <v-col v-if="isSplitWorkpackagesEnabled && !isTemplate" class="split-column pr-2">
        <WpScopeFilters />
      </v-col>
      <v-col :class="{ 'pl-2': isSplitWorkpackagesEnabled }" class="products-column pr-2">
        <WpScopeProductsNew />
      </v-col>
      <v-col class="pl-2">
        <WpScopeStoresNew />
      </v-col>
      <v-col>&nbsp;</v-col>
    </v-row>
  </div>
</template>

<script>
import { reactive } from 'vue';
import { get, isArray, keyBy, isEmpty, each, merge, partition } from 'lodash';
import { mapActions, mapState } from 'pinia';
import agGridUtils from '@/utils/ag-grid-utils';
import { useFeature } from '@/composables/use-feature';
import { usePermission } from '@/composables/use-permission';
import { useInternationalization } from '@/composables/use-internationalization';
import { useContextStore } from '@/stores/context';
import { useAssortmentGroupsStore } from '@/stores/assortment-groups';
import { useWorkpackagesStore } from '@/stores/workpackages';
import chainLevels from '@enums/chain-levels';

export default {
  props: {
    customWorkpackage: {
      type: Object,
      required: true,
    },
    datesLoaded: {
      type: Boolean,
      required: true,
    },
  },

  setup() {
    const { templatesEnabled, syntheticFurnitureEnabled, hideAnalysisOnlySelection } = useFeature();
    const { canEditWorkpackageScope } = usePermission();
    const { tkey } = useInternationalization('workpackagePage.scope.assortmentGroups');

    const assortmentGroupsStore = useAssortmentGroupsStore();

    return {
      assortmentGroupsStore,
      canEditWorkpackageScope,
      templatesEnabled,
      syntheticFurnitureEnabled,
      hideAnalysisOnlySelection,
      tkey,
    };
  },

  data() {
    return {
      searchString: null,
      isTemplateOnly: false,
      syntheticFurnitureFilter: this.syntheticFurnitureEnabled ? 'standard_only' : null,
      assortmentGroups: [],
      gridApi: null,
    };
  },

  computed: {
    ...mapState(useContextStore, ['clientConfig']),
    ...mapState(useAssortmentGroupsStore, ['masterAssortmentGroups']),
    ...mapState(useWorkpackagesStore, [
      'templatesById',
      'proposedAssortmentGroups',
      'wpDateSelections',
      'selectedWorkpackage',
    ]),

    isLoadingAssortmentGroups() {
      return this.assortmentGroupsStore.loading;
    },

    categoriesField() {
      return get(
        this.clientConfig,
        'assortmentGroups.filterType',
        'associatedProductCategoryDescriptions'
      );
    },

    isTemplate() {
      return !!this.selectedWorkpackage.isTemplate;
    },

    isSplitWorkpackagesEnabled() {
      return get(this.clientConfig, 'features.splitWorkpackagesEnabled');
    },

    gridOptions() {
      return {
        columnMenu: 'legacy',
        suppressContextMenu: true,
        isExternalFilterPresent: () =>
          !!this.searchString || this.isTemplateOnly || this.syntheticFurnitureFilter,
        cellSelection: { handle: { mode: 'range' } },
        defaultColDef: {
          filter: true,
          sortable: true,
          resizable: true,
          comparator: agGridUtils.sortings.naturalSort,
          editable: false,
          menuTabs: ['filterMenuTab'],
          suppressMovable: true,
          minWidth: 80,
          flex: 1,
          cellClass: 'aligned-start priority-cell',
          cellClassRules: {
            // TODO: check if this.hasDiff is redundant
            // 'diff-background': this.hasDiff,
            'invalid-nonempty': agGridUtils.validations.validateValueExists,
          },
          suppressKeyboardEvent: agGridUtils.utils.suppressKeyboardEvent,
        },
        rowHeight: 30,
        headerHeight: 40,
        processCellFromClipboard: agGridUtils.utils.processCellFromClipboard,
        getRowId: params => params.data.key,
      };
    },

    columnDefs() {
      return [
        {
          headerName: this.tkey('table.furnitureCategory'),
          field: 'text',
          width: 100,
        },
        {
          headerName: this.tkey('table.category'),
          field: this.categoriesField,
          width: 100,
        },
        {
          hide: !this.hasTemplateAvailable,
          headerName: this.tkey('table.fromTemplate'),
          field: 'isFromTemplate',
          resizable: false,
          maxWidth: 100,
          cellClass: 'justified-center',
          headerClass: 'justified-center',
          menuTabs: [],
          cellRenderer: params =>
            agGridUtils.utils.customIconRenderer(params, {
              icon: params.data.isFromTemplate ? 'check' : 'close',
              classes: params.data.isFromTemplate ? 'from-template' : 'not-from-template',
              isDisplayed: true,
            }),
        },
        {
          headerName: this.tkey('table.isSynthetic'),
          hide: !this.hasSyntheticFurnitureEnabled,
          field: 'hasSyntheticFurniture',
          filter: 'agSetColumnFilter',
          maxWidth: 100,
          editable: false,
          cellDataType: 'boolean',
          cellClass: 'justified-center',
          headerClass: 'justified-center',
          menuTabs: [],
        },
        {
          headerName: this.tkey('table.analysis'),
          hide: this.hideAnalysisOnlySelection,
          field: 'analysis',
          resizable: false,
          maxWidth: 100,
          editable: this.canEditWorkpackageScope,
          valueParser: agGridUtils.parsers.booleanParser,
          cellRenderer: params =>
            agGridUtils.utils.checkboxRenderer(params, this.handleCheckboxChange),
          cellRendererParams: { field: 'analysis' },
          onCellValueChanged: params => this.onCheckboxValueChanged(params),
          cellClass: 'justified-center',
          headerClass: 'justified-center',
          menuTabs: [],
        },
        {
          headerName: this.tkey('table.assortment'),
          field: 'assortment',
          resizable: false,
          maxWidth: 100,
          editable: this.canEditWorkpackageScope,
          valueParser: agGridUtils.parsers.booleanParser,
          cellRenderer: params =>
            agGridUtils.utils.checkboxRenderer(params, this.handleCheckboxChange),
          cellRendererParams: { field: 'assortment' },
          onCellValueChanged: params => this.onCheckboxValueChanged(params),
          cellClass: 'justified-center e2e-chk-ag-for-assortment',
          headerClass: 'justified-center',
          menuTabs: [],
        },
      ];
    },

    hasTemplateAvailable() {
      return this.templatesEnabled && !!get(this.selectedWorkpackage, 'templateId', null);
    },

    hasSyntheticFurnitureEnabled() {
      return this.syntheticFurnitureEnabled;
    },

    assortmentGroupsFromTemplate() {
      if (!this.selectedWorkpackage)
        return {
          staticAssortmentGroupKeys: new Set([]),
          assortmentGroupsSelectedFromTemplate: [],
        };
      const assortmentGroupsSelectedFromTemplate = get(
        this.templatesById,
        [this.selectedWorkpackage.templateId, 'assortmentGroups'],
        []
      );
      const staticAssortmentGroupKeys = new Set(
        assortmentGroupsSelectedFromTemplate.map(ag => ag.staticAssortmentGroupKey)
      );
      return {
        staticAssortmentGroupKeys,
        assortmentGroupsSelectedFromTemplate,
      };
    },

    selectedAssortmentGroupsByKey() {
      return keyBy(this.selectedWorkpackage.selectedAssortmentGroupSettings || [], 'key');
    },

    selectedAssortmentGroups() {
      return this.assortmentGroups.filter(ag => ag.analysis || ag.assortment);
    },

    selectedAssortmentGroupsForAnalysisOnly() {
      return this.assortmentGroups.filter(ag => ag.analysis && !ag.assortment);
    },

    selectedAssortmentGroupsForAssortment() {
      return this.assortmentGroups.filter(ag => ag.assortment);
    },
  },

  watch: {
    // This would run when changing periodIds or run auto populate
    proposedAssortmentGroups: {
      handler(proposals) {
        this.populateAssortmentGroups();
        this.setProposedAssortmentGroups(proposals);
      },
    },

    // This would run when changing snapshotdates
    masterAssortmentGroups: {
      handler() {
        this.populateAssortmentGroups();
        this.setProposedAssortmentGroups(this.proposedAssortmentGroups);
      },
      immediate: true,
    },
  },

  async created() {
    // Use the selectedWorkpackage's snapshotDate directly to ensure we fetch the correct AGs,
    // rather than relying on wpDateSelections which may have stale data during scope transitions.
    const snapshotDate = get(this.selectedWorkpackage, 'snapshotDate');
    await this.fetchAssortmentGroups(snapshotDate);
    this.populateAssortmentGroups();
  },

  methods: {
    ...mapActions(useAssortmentGroupsStore, ['fetchAssortmentGroups']),
    ...mapActions(useWorkpackagesStore, ['updateSelectedAssortmentGroups']),

    onGridReady(params) {
      this.gridApi = params.api;
    },

    populateAssortmentGroups() {
      const { staticAssortmentGroupKeys } = this.assortmentGroupsFromTemplate;

      this.assortmentGroups = this.masterAssortmentGroups.map(ag => {
        let agSettings = { analysis: false, assortment: false };

        if (this.selectedAssortmentGroupsByKey[ag.key]) {
          const { analysis, assortment } = this.selectedAssortmentGroupsByKey[ag.key];
          agSettings = { analysis, assortment };
        }

        return reactive({
          ...ag,
          ...agSettings,
          isFromTemplate: staticAssortmentGroupKeys.has(ag.staticAssortmentGroupKey),
        });
      });

      this.updateSelectedAssortmentGroups(this.selectedAssortmentGroups);
    },

    setProposedAssortmentGroups(proposal = {}) {
      const { selectedChain, proposedAGs } = proposal;
      if (isEmpty(proposedAGs)) return;

      const settings = {
        AB: {
          assortment: ['AB'],
          analysis: ['PM'],
        },
        PM: {
          assortment: ['PM'],
          analysis: ['SM'],
        },
        SM: {
          assortment: ['SM'],
          analysis: ['PM', 'PR'],
        },
        PR: {
          assortment: ['PR'],
          analysis: ['PM', 'SM'],
        },
        CFC: {
          assortment: ['PR'],
          analysis: [],
        },
      };

      // These settings must be kept in sync with enum workpackageSetupChainLevels
      if (
        !settings[selectedChain] ||
        !chainLevels.workpackageSetupChainLevels.includes(selectedChain)
      ) {
        console.error('Unhandled case for chain setup.');
      }

      const { assortment, analysis } = settings[selectedChain];

      const [cfcAgs, nonCfcAgs] = partition(proposedAGs, { assortmentSeries: 'CFC' });

      // If CFC, use only CFC options.
      const filteredAgs = selectedChain === 'CFC' ? cfcAgs : nonCfcAgs;

      const updates = {};

      filteredAgs.forEach(ag => {
        const inAnalysis = analysis.includes(ag.chain);

        if (inAnalysis) {
          updates[ag.key] = {
            assortment: false,
            analysis: true,
          };
        }
      });

      filteredAgs.forEach(ag => {
        const inAssortment = assortment.includes(ag.chain);

        if (inAssortment) {
          updates[ag.key] = {
            assortment: true,
            analysis: true,
          };
        }
      });

      // The table draws from this data, so update it.
      each(this.assortmentGroups, ag => {
        ag.assortment = false;
        ag.analysis = false;

        if (updates[ag.key]) {
          ag = merge(ag, updates[ag.key]);
        }
      });

      // The side-panels rely on this emit to be redrawn.
      this.updateSelectedAssortmentGroups(this.selectedAssortmentGroups);

      // This ensures ag-grid refreshes based on the updated data in this.assortmentGroups
      this.gridApi.refreshCells();
    },

    handleCheckboxChange(params) {
      params.setValue(!params.value);
    },

    doesExternalFilterPass(node) {
      let passTemplateFilter = true;
      if (this.hasTemplateAvailable && this.isTemplateOnly) {
        passTemplateFilter = node.data.isFromTemplate === true;
      }

      let passSearchFilter = true;
      if (this.searchString) {
        let { text } = node.data;
        let categories = node.data[this.categoriesField];

        text = agGridUtils.filters.standardStringFilter.textFormatter(text);
        if (isArray(categories)) {
          categories = categories.join(', ');
        }
        categories = agGridUtils.filters.standardStringFilter.textFormatter(categories);

        const searchString = agGridUtils.filters.standardStringFilter.textFormatter(
          this.searchString
        );
        passSearchFilter =
          text.indexOf(searchString) !== -1 || categories.indexOf(searchString) !== -1;
      }

      let passSyntheticFurnitureFilter = true;
      if (this.hasSyntheticFurnitureEnabled && this.syntheticFurnitureFilter) {
        if (this.syntheticFurnitureFilter === 'standard_only') {
          passSyntheticFurnitureFilter = !get(node.data, 'hasSyntheticFurniture');
        } else if (this.syntheticFurnitureFilter === 'synthetic_only') {
          passSyntheticFurnitureFilter = node.data.hasSyntheticFurniture === true;
        }
      }

      return passTemplateFilter && passSearchFilter && passSyntheticFurnitureFilter;
    },

    onCheckboxValueChanged(params) {
      const updates = { [params.colDef.field]: params.newValue };

      if (params.colDef.field === 'assortment' && params.newValue) {
        updates.analysis = true;
      }

      // If not using the analysis selection, then ensure it matches the assortment selection
      if (params.colDef.field === 'assortment' && this.hideAnalysisOnlySelection) {
        updates.analysis = params.newValue;
      }

      this.assortmentGroups = this.assortmentGroups.map(ag => {
        if (ag.key === params.data.key) {
          return {
            ...params.data,
            ...updates,
          };
        }

        return ag;
      });

      this.updateSelectedAssortmentGroups(this.selectedAssortmentGroups);
    },

    unselectAllAnalysis() {
      const keys = this.selectedAssortmentGroupsForAnalysisOnly.map(ag => ag.key);
      this.unselect('analysis', keys);
    },

    unselect(type, keys) {
      const updates = [];
      const agsToUpdate = this.assortmentGroups.filter(ag => keys.includes(ag.key));

      agsToUpdate.forEach(ag => {
        ag[type] = false;
        // If not showing the analysis section, also deselect for analysis
        if (type === 'assortment' && this.hideAnalysisOnlySelection) {
          ag.analysis = false;
        }
        updates.push(ag);
      });
      this.gridApi.applyTransaction({ update: updates });

      this.updateSelectedAssortmentGroups(this.selectedAssortmentGroups);
    },

    unselectAllAssortment() {
      const keys = this.selectedAssortmentGroupsForAssortment.map(ag => ag.key);
      this.unselect('assortment', keys);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.assortment-groups-tab {
  background-color: $assortment-workpackage-frame-bg-blue;
  color: $assortment-workpackage-text;

  .available-ags {
    border-right: 1px solid $assortment-tab-divider-colour;

    &__wrapper {
      background-color: $assortment-workpackage-bg-colour;
    }
  }

  .products-column,
  .split-column {
    border-right: 1px solid $assortment-tab-divider-colour;
  }

  &__text {
    font-size: 12px;
  }

  .assortment-groups-tab__link {
    color: $assortment-primary-colour;
    padding: 0 !important;
    &::before,
    &::after {
      display: none !important;
    }

    span {
      text-decoration: underline;
    }
  }

  .grid-wrapper {
    height: 140px;
    overflow: auto;
    margin: 8px 0;
    background-color: $assortment-table-header-white-background;

    .grid {
      display: grid;
      grid-template-columns: 1fr 32px;
      grid-gap: 1px;
      background-color: $assortment-workpackage-frame-bg-blue;
      border-bottom: 1px solid $assortment-workpackage-frame-bg-blue;

      .display-contents {
        display: contents;
      }

      .item {
        padding: 4px 8px;
        background-color: $assortment-table-header-white-background;
        line-height: 16px;
        font-size: 12px;

        &__icon {
          padding-top: 2px;
          padding-bottom: 0;
        }
      }
    }
  }

  .grid-wrapper-extended {
    height: 300px;
  }

  .search {
    :deep(.v-input) {
      width: auto !important;
    }
  }

  p {
    font-weight: normal;
    font-size: 12px;
  }
}

:deep(.from-template) {
  color: $assortment-workpackage-green-mid;
}
:deep(.not-from-template) {
  color: $assortment-workpackage-disabled;
}

:deep(.ag-theme-alpine .ag-row-even) {
  background: inherit;
}

:deep(
    .ag-theme-alpine
      .ag-cell.ag-cell-not-inline-editing.ag-cell-value.ag-cell-focus.ag-cell-range-single-cell
  ) {
  padding-left: 0 !important;
}
</style>
